import { useContext, useMemo } from 'react'

import {
  bookerProgression,
  BookingStage,
  bookingStages,
} from '@/lib/stores/booker-store'
import { RootStoreContext } from '@/lib/stores/root-store'

export const useBookerNav = () => {
  const {
    bookerStore: {
      stage,
      paymentCollectionType,
      selectedDate,
      selectedTimeslot,
      selectedUserId,
      bookingId,
      eventTypeId,
      memberOptionsData,
      isCustomBooking,
      customPrice,
    },
  } = useContext(RootStoreContext)

  const stages = useMemo(() => {
    if (eventTypeId) {
      return bookingStages.filter((stage) => stage !== 'SERVICES')
    }

    if (isCustomBooking) {
      return bookingStages.filter(
        (stage) => stage !== 'SERVICES' && stage !== 'MEMBER_PICKER',
      )
    }

    return bookingStages
  }, [isCustomBooking, eventTypeId])

  const nextStage = useMemo(() => {
    let nextStage: BookingStage | null = null

    if (stage === null || stage === 'SERVICES') {
      nextStage = 'MEMBER_PICKER'
    } else {
      nextStage = stages[stages.indexOf(stage) + 1]
    }

    if (nextStage) {
      const logic = bookerProgression[nextStage]
      if (
        logic.skip &&
        logic.skip({
          selectedUserId,
          selectedDate,
          selectedTimeslot,
          bookingId,
          paymentCollectionType,
          memberOptionsData,
          customPrice: customPrice ?? undefined,
        })
      ) {
        nextStage = stages[stages.indexOf(nextStage) + 1]
      }
    }

    return nextStage
  }, [
    bookingId,
    customPrice,
    memberOptionsData,
    paymentCollectionType,
    selectedDate,
    selectedTimeslot,
    selectedUserId,
    stage,
    stages,
  ])

  const previousStage = useMemo(() => {
    if (stage === null || stage === 'SERVICES') return null

    let prevStage = stages[stages.indexOf(stage) - 1]

    if (prevStage) {
      const logic = bookerProgression[prevStage]
      if (
        logic.skip &&
        logic.skip({
          selectedUserId,
          selectedDate,
          selectedTimeslot,
          bookingId,
          paymentCollectionType,
          memberOptionsData,
          customPrice: customPrice ?? undefined,
        })
      ) {
        const prevIndex = stages.indexOf(prevStage) - 1
        prevStage = prevIndex >= 0 ? stages[prevIndex] : stages[0]
      }
    }

    return prevStage
  }, [
    stage,
    stages,
    bookingId,
    customPrice,
    memberOptionsData,
    paymentCollectionType,
    selectedDate,
    selectedTimeslot,
    selectedUserId,
  ])

  const isFirstStage = useMemo(() => {
    return stage === null || stages[0] === stage
  }, [stage, stages])

  return { nextStage, previousStage, isFirstStage }
}
